import React, { useState, useCallback } from 'react';
import PageWrapper from '../components/ui/PageWrapper';
import FadeUp from '../components/ui/FadeUp';
import Toast from '../components/ui/Toast';
import { useContent } from '../hooks/useContent';

export default function Contact() {
  const { content } = useContent();
  const c = content?.contact;

  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const closeToast = useCallback(() => setToast(null), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Failed');
      setSubmitted(true);
      setToast({ message: 'Your enquiry has been submitted! We will contact you within 24 hours. 🙏', type: 'success' });
    } catch {
      setToast({ message: 'Something went wrong. Please try again or contact us directly.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '14px 16px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(232,135,26,0.2)',
    borderRadius: 8, color: '#FDF6EC',
    fontFamily: 'Inter, sans-serif', fontSize: 14,
    outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  };

  const contactInfo = c?.info ?? [
    { icon: '📞', label: 'Call Us', value: 'Get in touch for consultation' },
    { icon: '✉️', label: 'Email', value: 'info@theperfectvaastu.com' },
    { icon: '🕐', label: 'Hours', value: 'Mon–Sat: 10am – 7pm IST' },
  ];

  const whyUs = c?.whyUs ?? [
    'Certified Vastu Consultant', 'Practical & result-oriented solutions',
    'Personalized consultations', 'Traditional wisdom + modern approach', 'Trained under Vastu RaviRaj Sir'
  ];

  const serviceOptions = c?.serviceOptions ?? [];
  const success = c?.successMessage;

  return (
    <PageWrapper>
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      <section style={{ paddingTop: 140, paddingBottom: 80, background: 'linear-gradient(135deg, #0D0D1F, #1A0F2E)', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 60% 40%, rgba(232,135,26,0.06) 0%, transparent 60%)' }} />
        <div className="container">
          <FadeUp>
            <p className="section-eyebrow">{c?.eyebrow ?? 'Get in Touch'}</p>
            <h1 className="section-title" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
              {c?.title ?? 'Book a Consultation'}
            </h1>
            <div className="divider" />
            <p style={{ fontSize: 16, opacity: 0.7, maxWidth: 520, lineHeight: 1.8 }}>
              {c?.subtitle}
            </p>
          </FadeUp>
        </div>
      </section>

      <section style={{ padding: '80px 0', background: '#0D0D1F' }}>
        <div className="container">
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 64 }}>
            <FadeUp>
              <div>
                <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 24, marginBottom: 32 }}>Contact Information</h2>
                {contactInfo.map((item: any, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 24, padding: '20px', background: 'rgba(232,135,26,0.03)', border: '1px solid rgba(232,135,26,0.1)', borderRadius: 10 }}>
                    <span style={{ fontSize: 20 }}>{item.icon}</span>
                    <div>
                      <p style={{ fontSize: 11, color: '#E8871A', letterSpacing: 1, marginBottom: 4 }}>{item.label.toUpperCase()}</p>
                      <p style={{ fontSize: 14, opacity: 0.8 }}>{item.value}</p>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 40 }}>
                  <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, color: '#E8871A', marginBottom: 16 }}>Why Choose Us?</p>
                  {whyUs.map((item: string, i: number) => (
                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                      <span style={{ color: '#E8871A' }}>✦</span>
                      <span style={{ fontSize: 14, opacity: 0.75 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              {submitted ? (
                <div style={{ background: 'rgba(232,135,26,0.06)', border: '1px solid rgba(232,135,26,0.2)', borderRadius: 16, padding: '60px 40px', textAlign: 'center' }}>
                  <p style={{ fontSize: 48, marginBottom: 20 }}>{success?.icon ?? '🙏'}</p>
                  <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 26, color: '#E8871A', marginBottom: 16 }}>
                    {success?.title ?? 'Thank You!'}
                  </h2>
                  <p style={{ fontSize: 15, opacity: 0.7, lineHeight: 1.8 }}>{success?.text}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(232,135,26,0.12)', borderRadius: 16, padding: '40px 36px' }}>
                  <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, marginBottom: 28 }}>Enquiry Form</h2>
                  <div className="contact-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ fontSize: 12, opacity: 0.6, display: 'block', marginBottom: 6 }}>Full Name *</label>
                      <input required style={inputStyle} placeholder="Your name"
                        value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                        onFocus={e => (e.target.style.borderColor = '#E8871A')}
                        onBlur={e => (e.target.style.borderColor = 'rgba(232,135,26,0.2)')} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, opacity: 0.6, display: 'block', marginBottom: 6 }}>Phone *</label>
                      <input required style={inputStyle} placeholder="+91 XXXXX XXXXX"
                        value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                        onFocus={e => (e.target.style.borderColor = '#E8871A')}
                        onBlur={e => (e.target.style.borderColor = 'rgba(232,135,26,0.2)')} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, opacity: 0.6, display: 'block', marginBottom: 6 }}>Email *</label>
                    <input required type="email" style={inputStyle} placeholder="your@email.com"
                      value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                      onFocus={e => (e.target.style.borderColor = '#E8871A')}
                      onBlur={e => (e.target.style.borderColor = 'rgba(232,135,26,0.2)')} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, opacity: 0.6, display: 'block', marginBottom: 6 }}>Service Required</label>
                    <select style={{ ...inputStyle, cursor: 'pointer' }}
                      value={form.service} onChange={e => setForm({ ...form, service: e.target.value })}>
                      <option value="" style={{ background: '#1A0F2E' }}>Select a service</option>
                      {serviceOptions.map((opt: any) => (
                        <option key={opt.value} value={opt.value} style={{ background: '#1A0F2E' }}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ fontSize: 12, opacity: 0.6, display: 'block', marginBottom: 6 }}>Message</label>
                    <textarea rows={4} style={{ ...inputStyle, resize: 'vertical' }}
                      placeholder="Describe your space and concerns..."
                      value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                      onFocus={e => (e.target.style.borderColor = '#E8871A')}
                      onBlur={e => (e.target.style.borderColor = 'rgba(232,135,26,0.2)')} />
                  </div>
                  <button type="submit" className="btn-primary" disabled={loading}
                    style={{ width: '100%', textAlign: 'center', opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                    {loading ? 'Sending…' : 'Submit Enquiry'}
                  </button>
                </form>
              )}
            </FadeUp>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 1024px) { .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; } }
        @media (max-width: 768px) { .contact-grid { grid-template-columns: 1fr !important; gap: 32px !important; } .contact-form-row { grid-template-columns: 1fr !important; } }
      `}</style>
    </PageWrapper>
  );
}
